export type ModeName = "Questions" | "Role play" | "Conversational";
export type OriginKey = "workspace" | "scorm" | "public_link";

export type Avatar = {
  id: string;
  name: string;
  mode: ModeName;
  is_scorable: boolean;
  pass_criteria: number | null;
  sessions: number;
  uniqueLearners: number;
  completionRate: number;
  passRate: number | null;
  avgScore: number | null;
  streamingTotal: number;
  streamingAvg: number;
  origin: Record<OriginKey, number>;
  scoreDist: { r: string; n: number; pass: boolean }[] | null;
};

export type Session = {
  id: string;
  avatar: string;
  learnerId: string | null;
  learner: string;
  /** Avatar thumbnail resolved from the Powtoon user endpoint (live mode only). */
  learnerThumb?: string | null;
  origin: OriginKey;
  min: number;
  completed: boolean;
  score: number | null;
  pass: boolean | null;
  when: string;
  ts: number;
};

export const AVATARS: Avatar[] = [
  {
    id: "compliance",
    name: "Compliance Certification 2026",
    mode: "Questions",
    is_scorable: true,
    pass_criteria: 80,
    sessions: 342,
    uniqueLearners: 298,
    completionRate: 87,
    passRate: 74,
    avgScore: 82,
    streamingTotal: 1240,
    streamingAvg: 3.6,
    origin: { workspace: 44, scorm: 210, public_link: 88 },
    scoreDist: [
      { r: "0-39", n: 9, pass: false },
      { r: "40-59", n: 21, pass: false },
      { r: "60-79", n: 58, pass: false },
      { r: "80-89", n: 104, pass: true },
      { r: "90-100", n: 105, pass: true },
    ],
  },
  {
    id: "objection",
    name: "Objection-Handling Roleplay",
    mode: "Role play",
    is_scorable: false,
    pass_criteria: null,
    sessions: 128,
    uniqueLearners: 96,
    completionRate: 71,
    passRate: null,
    avgScore: null,
    streamingTotal: 690,
    streamingAvg: 5.4,
    origin: { workspace: 71, scorm: 0, public_link: 57 },
    scoreDist: null,
  },
  {
    id: "product",
    name: "Product Knowledge Advisor",
    mode: "Conversational",
    is_scorable: false,
    pass_criteria: null,
    sessions: 205,
    uniqueLearners: 151,
    completionRate: 64,
    passRate: null,
    avgScore: null,
    streamingTotal: 812,
    streamingAvg: 4.0,
    origin: { workspace: 120, scorm: 0, public_link: 85 },
    scoreDist: null,
  },
];

export const SESSIONS: Session[] = [
  { id: "s1", avatar: "compliance", learnerId: "u_2231", learner: "Dana Kessler", origin: "scorm", min: 4.2, completed: true, score: 88, pass: true, when: "Jul 11, 09:14", ts: 20260711091 },
  { id: "s2", avatar: "compliance", learnerId: "u_2288", learner: "Marco Ruiz", origin: "scorm", min: 3.1, completed: true, score: 71, pass: false, when: "Jul 11, 08:52", ts: 20260711085 },
  { id: "s3", avatar: "objection", learnerId: null, learner: "Anonymous", origin: "public_link", min: 6.1, completed: true, score: null, pass: null, when: "Jul 11, 08:40", ts: 20260711084 },
  { id: "s4", avatar: "compliance", learnerId: "u_2301", learner: "Priya Nair", origin: "workspace", min: 3.9, completed: true, score: 94, pass: true, when: "Jul 10, 17:22", ts: 20260710172 },
  { id: "s5", avatar: "product", learnerId: null, learner: "Anonymous", origin: "public_link", min: 2.4, completed: false, score: null, pass: null, when: "Jul 10, 16:03", ts: 20260710160 },
  { id: "s6", avatar: "objection", learnerId: "u_1904", learner: "Tom Hardy", origin: "workspace", min: 7.8, completed: true, score: null, pass: null, when: "Jul 10, 15:31", ts: 20260710153 },
  { id: "s7", avatar: "compliance", learnerId: null, learner: "Anonymous", origin: "public_link", min: 2.9, completed: false, score: null, pass: null, when: "Jul 10, 14:10", ts: 20260710141 },
  { id: "s8", avatar: "product", learnerId: "u_2110", learner: "Sara Lin", origin: "workspace", min: 4.6, completed: true, score: null, pass: null, when: "Jul 9, 11:48", ts: 20260709114 },
  { id: "s9", avatar: "compliance", learnerId: "u_2044", learner: "Ben Ofek", origin: "scorm", min: 4.7, completed: true, score: 83, pass: true, when: "Jul 9, 10:05", ts: 20260709100 },
];

export const WEEKS = ["May 24", "May 31", "Jun 7", "Jun 14", "Jun 21", "Jun 28", "Jul 5", "Jul 12"];
export const WK_MINUTES = [238, 262, 295, 321, 356, 382, 405, 483];
export const WK_LEARNERS = [40, 44, 49, 55, 60, 64, 70, 78];

export const ORIGIN_META: Record<OriginKey, { label: string; icon: string; color: string }> = {
  workspace: { label: "Powtoon workspace", icon: "grid_view", color: "#3A57FA" },
  scorm: { label: "SCORM / LMS", icon: "school", color: "#854CE0" },
  public_link: { label: "Public access", icon: "link", color: "#1BAF70" },
};

export const MODE_META: Record<ModeName, { cls: string; icon: string; label: string }> = {
  Questions: { cls: "questions", icon: "assignment_turned_in", label: "Open questions" },
  "Role play": { cls: "roleplay", icon: "manage_accounts", label: "Role-play" },
  Conversational: { cls: "conversational", icon: "forum", label: "Conversational" },
};

export const avatarById = (id: string) => AVATARS.find((a) => a.id === id);
export const fmt = (n: number) => n.toLocaleString("en-US");

export function downloadTranscript(sid: string) {
  // Scaled ranges clone baseline sessions with an `-N` suffix; the transcript
  // content comes from the baseline row.
  const baseId = sid.split("-")[0]!;
  const s = SESSIONS.find((x) => x.id === baseId);
  if (!s) return;
  const a = avatarById(s.avatar)!;
  const lines = [
    `Live Avatar session transcript`,
    `Avatar: ${a.name} (${a.mode}${a.is_scorable ? ", scorable" : ""})`,
    `Learner: ${s.learner}${s.learnerId ? " [" + s.learnerId + "]" : " [anonymous]"}`,
    `Date: ${s.when}   Origin: ${ORIGIN_META[s.origin].label}   Duration: ${s.min} min   Completed: ${s.completed ? "Yes" : "No"}`,
    a.is_scorable && s.score !== null
      ? `Score: ${s.score}%   Pass: ${s.pass ? "Yes" : "No"} (threshold ${a.pass_criteria}%)`
      : `Score: N/A (non-scorable)`,
    ``,
    `--- Transcript ---`,
    `[00:03] Avatar: Welcome. Ready to begin?`,
    `[00:11] ${s.learner}: Yes, ready.`,
    `[00:24] Avatar: ...`,
  ].join("\n");
  const blob = new Blob([lines], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `transcript_${s.id}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}
